import { type PropsWithChildren, useMemo, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";

import { renderChevronAnimation } from "@shared/selects/utils";

export interface AccordionProps {
  title: string;
  isExpandedByDefault?: boolean;
  controlProps?: {
    expanded: boolean;
    setExpanded: (expanded: boolean) => void;
  };
}

export function Accordion({
  title,
  isExpandedByDefault,
  controlProps,
  children,
}: PropsWithChildren<AccordionProps>) {
  // If controlProps are provided, use them instead of the default state
  const [internalIsExpanded, setInternalExpanded] = useState(
    isExpandedByDefault ?? false,
  );

  const isExpanded = useMemo(
    () => (controlProps ? controlProps?.expanded : internalIsExpanded),
    [controlProps, internalIsExpanded],
  );
  const { setExpanded: manuallySetExpanded } = controlProps ?? {};

  return (
    <div className="border-b border-gray-100 mx-8">
      <h4
        className="text-[1rem] font-semibold m-0 flex items-center pt-5 pb-5 cursor-pointer"
        onClick={() => {
          manuallySetExpanded?.(!isExpanded) ||
            setInternalExpanded(!isExpanded);
        }}
      >
        <span className="flex-1">{title}</span>
        {renderChevronAnimation(isExpanded)}
      </h4>
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ ease: "easeOut", duration: 0.2 }}
            className="overflow-hidden px-[2rem] mx-[-2rem]"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
